import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {EndlessList} from 'components/ui-kit/EndlessList';
import {Table} from 'components/ui-kit/Table';
import {commonDataState$, Gate, productsTableState$} from 'models/orders/detail';
import {breakpoint} from 'theme';
import {PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {
    OrderDetailProductsFormattedRow,
    OrderDetailProductsRaw,
    OrdersDetailProductsAssessors,
} from 'types/orders';
import {useMatchMedia} from 'utils/custom-hooks';
import {handleNil} from 'utils/handleNil';

import {CodeExportControl} from './CodeExportControl';

import {FlexRowElement, ListRow, Quantity, Text} from './styled';

export function convertData(
    data: OrderDetailProductsRaw[] | undefined,
): OrderDetailProductsFormattedRow[] {
    if (!data) return [];

    return data.map((elem) => ({
        [OrdersDetailProductsAssessors.productId]: handleNil(elem?.productId),
        [OrdersDetailProductsAssessors.productName]: handleNil(elem?.productName),
        [OrdersDetailProductsAssessors.quantity]: handleNil(elem?.quantity, (value) => (
            <Quantity>{value}</Quantity>
        )),
        [OrdersDetailProductsAssessors.packageType]: handleNil(elem?.packageType),
        [OrdersDetailProductsAssessors.productGroupCode]: [
            ...TRANSPORT_PACKAGE_TYPES,
            PackageTypes.CUSTOMS_UNIT,
        ].includes(elem.packageType)
            ? '—'
            : handleNil(elem.productGroupCode, (value) => <ProductGroupIcon value={value} label />),
        [OrdersDetailProductsAssessors.availableQuantity]: handleNil(elem?.availableQuantity),
        [OrdersDetailProductsAssessors.gtin]: handleNil(elem?.gtin),
        [OrdersDetailProductsAssessors.printed]: elem.printed,
    }));
}

const CodeExportCell = ({row}: {row: {original: OrderDetailProductsFormattedRow}}) => {
    const {id} = useStore(Gate.state);
    const {data} = useStore(commonDataState$);
    return (
        <CodeExportControl
            orderId={id}
            gtin={row.original.gtin}
            packageType={row.original.packageType}
            printed={row.original.printed}
            status={data?.status}
        />
    );
};

export const Products = () => {
    const {id} = useStore(Gate.state);

    const getConfig = (t: TFunction) => [
        {
            Header: t('pages.orders.productGroup'),
            accessor: OrdersDetailProductsAssessors.productGroupCode,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.form.type'),
            accessor: OrdersDetailProductsAssessors.packageType,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.detail.productsTab.productName'),
            accessor: OrdersDetailProductsAssessors.productName,
            width: 170,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.detail.productsTab.quantity'),
            accessor: OrdersDetailProductsAssessors.quantity,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.detail.productsTab.gtin'),
            accessor: OrdersDetailProductsAssessors.gtin,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: 'Action',
            accessor: 'action-print',
            Cell: CodeExportCell,
            disableSortBy: true,
        },
    ];

    const {t} = useTranslation();
    const {data, isLoading} = useStore(productsTableState$);
    const {data: commonData} = useStore(commonDataState$);
    const status = commonData?.status;
    const columns = React.useMemo(() => getConfig(t), [t]);
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);

    const mappedData = React.useMemo(() => convertData(data), [data, t]);

    return (
        <div>
            {!isMobile && (
                <Table
                    columns={columns}
                    data={mappedData}
                    totalRowsCount={data.length}
                    isLoading={isLoading}
                    hideSetting
                />
            )}

            {isMobile && (
                <EndlessList<OrderDetailProductsFormattedRow>
                    rows={mappedData}
                    totalRowsCount={data.length}
                    renderRow={(row: OrderDetailProductsFormattedRow) => (
                        <ListRow>
                            <FlexRowElement>
                                <Text isGray>{row[OrdersDetailProductsAssessors.productName]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text>{row[OrdersDetailProductsAssessors.productId]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text isGray>{row[OrdersDetailProductsAssessors.gtin]}</Text>
                                <Text isGray>{row[OrdersDetailProductsAssessors.quantity]}</Text>
                            </FlexRowElement>
                            <CodeExportControl
                                orderId={id}
                                gtin={row.gtin}
                                packageType={row.packageType}
                                printed={row.printed}
                                status={status}
                            />
                        </ListRow>
                    )}
                    config={columns}
                />
            )}
        </div>
    );
};
